import { useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Users, UserPlus, X, Mail, Lock, User, UserCheck, Building2, Shield, ChevronDown, ChevronUp, Trash2, AlertTriangle, RefreshCw, Copy, Eye, EyeOff, KeyRound, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const generateRandomPassword = (): string => {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const digits = "23456789";
  const special = "!@#$%&*";
  const all = upper + lower + digits + special;
  let pw = "";
  pw += upper[Math.floor(Math.random() * upper.length)];
  pw += lower[Math.floor(Math.random() * lower.length)];
  pw += digits[Math.floor(Math.random() * digits.length)];
  pw += special[Math.floor(Math.random() * special.length)];
  for (let i = 4; i < 14; i++) pw += all[Math.floor(Math.random() * all.length)];
  return pw.split("").sort(() => Math.random() - 0.5).join("");
};

const EDGE_BASE = `https://zxdwkagqcqojxcwlsdyc.supabase.co/functions/v1`;

const getAuthHeader = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return { "Authorization": `Bearer ${session?.access_token}`, "Content-Type": "application/json" };
};

const createAccount = async (email: string, password: string, fullName: string, companyId: string, role: string) => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/create-employee`, { method: "POST", headers, body: JSON.stringify({ email, password, fullName, companyId, role }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to create account");
  return data;
};

const fetchUnassigned = async () => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-users?action=unassigned`, { headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to fetch unassigned users");
  return data.users as { id: string; email: string; full_name: string | null; created_at: string }[];
};

const assignUser = async (userId: string, companyId: string, fullName?: string) => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-users?action=assign`, { method: "POST", headers, body: JSON.stringify({ userId, companyId, fullName }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to assign user");
  return data;
};

const reassignUser = async (userId: string, companyId: string) => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-users?action=reassign`, { method: "POST", headers, body: JSON.stringify({ userId, companyId }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to reassign user");
  return data;
};

const updateUserRole = async (userId: string, role: string) => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-users?action=update-role`, { method: "POST", headers, body: JSON.stringify({ userId, role }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to update role");
  return data;
};

const updateUserPermissions = async (userId: string, permissions: Record<string, boolean>) => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-users?action=update-permissions`, { method: "POST", headers, body: JSON.stringify({ userId, permissions }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to update permissions");
  return data;
};

const deleteUser = async (userId: string) => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-users?action=delete`, { method: "POST", headers, body: JSON.stringify({ userId }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to delete user");
  return data;
};

const resetUserPassword = async (userId: string, newPassword: string) => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-users?action=reset-password`, { method: "POST", headers, body: JSON.stringify({ userId, newPassword }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to reset password");
  return data;
};

const fetchAuthMeta = async () => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-users?action=auth-meta`, { headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to fetch auth metadata");
  return data.users as { id: string; email: string; last_sign_in_at: string | null; created_at: string }[];
};

type UnassignedUser = { id: string; email: string; full_name: string | null; created_at: string };

interface UserPerms {
  user_id: string;
  page_dashboard: boolean;
  page_documents: boolean;
  page_progress: boolean;
  page_questions: boolean;
  page_fleet: boolean;
  page_voyage_ops: boolean;
  page_compliance: boolean;
  page_commercial: boolean;
  page_reports: boolean;
  page_satisfaction: boolean;
  page_crew_personnel: boolean;
  page_esg: boolean;
  page_training: boolean;
  page_emergency: boolean;
  page_maintenance: boolean;
  page_messages: boolean;
  page_news: boolean;
  page_lessons_learned: boolean;
  page_cti: boolean;
}

const AdminUsers = () => {
  const qc = useQueryClient();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createRole, setCreateRole] = useState<"employee" | "client">("employee");
  const [createEmail, setCreateEmail] = useState("");
  const [createPassword, setCreatePassword] = useState(generateRandomPassword());
  const [showPassword, setShowPassword] = useState(true);
  const [createName, setCreateName] = useState("");
  const [createCompany, setCreateCompany] = useState("");

  const [showAssignSection, setShowAssignSection] = useState(false);
  const [assignCompanies, setAssignCompanies] = useState<Record<string, string>>({});
  const [editingCompany, setEditingCompany] = useState<Record<string, string>>({});

  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [resetPwUser, setResetPwUser] = useState<string | null>(null);
  const [resetPwValue, setResetPwValue] = useState("");
  const [showResetPw, setShowResetPw] = useState(true);

  const { data: companies = [] } = useQuery({
    queryKey: ["admin-companies"],
    queryFn: async () => {
      const { data } = await supabase.from("companies").select("*").order("name");
      return data ?? [];
    },
  });

  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ["admin-profiles"],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("*, companies(name)")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const { data: roles = [] } = useQuery({
    queryKey: ["admin-user-roles"],
    queryFn: async () => {
      const { data } = await supabase.from("user_roles").select("*");
      return data ?? [];
    },
  });

  const { data: allPermissions = [] } = useQuery<UserPerms[]>({
    queryKey: ["admin-user-permissions"],
    queryFn: async () => {
      const { data } = await supabase.from("user_permissions").select("*");
      return (data ?? []) as UserPerms[];
    },
  });

  const { data: authMeta = [] } = useQuery({
    queryKey: ["admin-auth-meta"],
    queryFn: fetchAuthMeta,
  });

  const { data: unassigned = [], isLoading: unassignedLoading } = useQuery<UnassignedUser[]>({
    queryKey: ["admin-unassigned-users"],
    queryFn: fetchUnassigned,
    enabled: showAssignSection,
  });

  const addAccount = useMutation({
    mutationFn: () => createAccount(createEmail, createPassword, createName, createCompany, createRole),
    onSuccess: () => {
      setShowCreateForm(false);
      setCreateEmail(""); setCreatePassword(generateRandomPassword()); setCreateName(""); setCreateCompany(""); setCreateRole("employee");
      qc.invalidateQueries({ queryKey: ["admin-profiles"] });
      qc.invalidateQueries({ queryKey: ["admin-user-roles"] });
      qc.invalidateQueries({ queryKey: ["admin-user-count"] });
      qc.invalidateQueries({ queryKey: ["admin-approvals"] });
      toast.success(`${createRole === "employee" ? "Employee" : "Client"} account created`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const doAssign = useMutation({
    mutationFn: ({ userId, companyId }: { userId: string; companyId: string }) => assignUser(userId, companyId),
    onSuccess: (_, { userId }) => {
      setAssignCompanies((prev) => { const n = { ...prev }; delete n[userId]; return n; });
      qc.invalidateQueries({ queryKey: ["admin-profiles"] });
      qc.invalidateQueries({ queryKey: ["admin-user-roles"] });
      qc.invalidateQueries({ queryKey: ["admin-unassigned-users"] });
      qc.invalidateQueries({ queryKey: ["admin-user-count"] });
      toast.success("User assigned to company");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const doReassign = useMutation({
    mutationFn: ({ userId, companyId }: { userId: string; companyId: string }) => reassignUser(userId, companyId),
    onSuccess: (_, { userId }) => {
      setEditingCompany((prev) => { const n = { ...prev }; delete n[userId]; return n; });
      qc.invalidateQueries({ queryKey: ["admin-profiles"] });
      toast.success("Company updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const doUpdateRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => updateUserRole(userId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user-roles"] });
      toast.success("Role updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const doUpdatePerms = useMutation({
    mutationFn: ({ userId, permissions }: { userId: string; permissions: Record<string, boolean> }) => updateUserPermissions(userId, permissions),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user-permissions"] });
      toast.success("Permissions updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const doDelete = useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["admin-profiles"] });
      qc.invalidateQueries({ queryKey: ["admin-user-roles"] });
      qc.invalidateQueries({ queryKey: ["admin-user-permissions"] });
      qc.invalidateQueries({ queryKey: ["admin-user-count"] });
      qc.invalidateQueries({ queryKey: ["admin-approvals"] });
      qc.invalidateQueries({ queryKey: ["admin-employees"] });
      toast.success("User account deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const doResetPassword = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) => resetUserPassword(userId, newPassword),
    onSuccess: () => {
      toast.success("Password reset successfully. User will be prompted to change it on next login.");
      setResetPwUser(null);
      setResetPwValue("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const getRoleLabel = (userId: string) => {
    const role = roles.find((r) => r.user_id === userId);
    return role?.role ?? "client";
  };

  const getLastLogin = (userId: string) => {
    const meta = authMeta.find((m) => m.id === userId);
    return meta?.last_sign_in_at ?? null;
  };

  const defaultPerms: UserPerms = {
    user_id: "", page_dashboard: true, page_documents: true, page_progress: true, page_questions: true,
    page_fleet: true, page_voyage_ops: true, page_compliance: true, page_commercial: true,
    page_reports: true, page_satisfaction: true, page_crew_personnel: true, page_esg: true,
    page_training: true, page_emergency: true, page_maintenance: true, page_messages: true,
    page_news: true, page_lessons_learned: true, page_cti: true,
  };

  const getPerms = (userId: string): UserPerms => {
    const found = allPermissions.find((p) => p.user_id === userId);
    return found ?? { ...defaultPerms, user_id: userId };
  };

  const roleBadge: Record<string, string> = {
    admin: "bg-teal/15 text-teal",
    employee: "bg-lime/15 text-lime",
    client: "bg-primary-foreground/8 text-primary-foreground/40",
    auditor: "bg-amber-400/15 text-amber-300",
  };

  const pageLabels = [
    { key: "page_dashboard", label: "Dashboard" },
    { key: "page_fleet", label: "Fleet & Vessels" },
    { key: "page_voyage_ops", label: "Voyage Ops" },
    { key: "page_crew_personnel", label: "Crew & Personnel" },
    { key: "page_cti", label: "Crew Transfer Intel" },
    { key: "page_compliance", label: "HSEQ & Compliance" },
    { key: "page_training", label: "Training" },
    { key: "page_emergency", label: "Emergency & SAR" },
    { key: "page_commercial", label: "Commercial" },
    { key: "page_documents", label: "Documents" },
    { key: "page_esg", label: "ESG & Emissions" },
    { key: "page_reports", label: "Reports" },
    { key: "page_satisfaction", label: "Satisfaction" },
    { key: "page_maintenance", label: "Maintenance" },
    { key: "page_lessons_learned", label: "Lessons Learned" },
    { key: "page_messages", label: "Messages" },
    { key: "page_news", label: "News" },
    { key: "page_questions", label: "Questions" },
    { key: "page_progress", label: "Progress" },
  ];

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Admin</p>
          <h1 className="font-display text-3xl text-primary-foreground tracking-tight">
            Client <span className="accent-text">Users</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowAssignSection(!showAssignSection); setShowCreateForm(false); }}
            className="flex items-center gap-2 px-5 py-2.5 border border-teal/30 text-teal text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:bg-teal/5 transition-all font-body"
          >
            <UserCheck className="w-4 h-4" />
            Assign Client
          </button>
          <button
            onClick={() => { 
              if (!showCreateForm) setCreatePassword(generateRandomPassword());
              setShowCreateForm(!showCreateForm); 
              setShowAssignSection(false); 
            }}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body"
          >
            {showCreateForm ? <X className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
            {showCreateForm ? "Cancel" : "Create Account"}
          </button>
        </div>
      </div>

      {/* Create Account Form */}
      {showCreateForm && (
        <div className="glass-card rounded-2xl p-6 mb-6">
          <h2 className="font-display text-lg text-primary-foreground mb-1">Create New Account</h2>
          <p className="text-primary-foreground/30 text-sm font-body mb-5">Create an employee or client account. The account will be auto-approved.</p>

          {/* Role selector */}
          <div className="flex gap-2 mb-5">
            {(["employee", "client"] as const).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setCreateRole(r)}
                className={`px-4 py-2 text-[11px] font-bold tracking-[1.5px] uppercase rounded-xl font-body transition-all ${
                  createRole === r
                    ? r === "employee" ? "bg-lime/15 text-lime border border-lime/30" : "bg-teal/15 text-teal border border-teal/30"
                    : "bg-primary-foreground/5 text-primary-foreground/30 border border-primary-foreground/10 hover:text-primary-foreground/50"
                }`}
              >
                {r}
              </button>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); addAccount.mutate(); }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-teal/50" />
              <input type="text" value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="Full name" required
                className="w-full pl-11 pr-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all" />
            </div>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-teal/50" />
              <input type="email" value={createEmail} onChange={(e) => setCreateEmail(e.target.value)} placeholder="Email address" required
                className="w-full pl-11 pr-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all" />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-teal/50" />
              <input type={showPassword ? "text" : "password"} value={createPassword} onChange={(e) => setCreatePassword(e.target.value)} placeholder="Temporary password" required minLength={6}
                className="w-full pl-11 pr-28 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm font-mono placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all" />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="p-1.5 rounded-lg hover:bg-primary-foreground/10 text-primary-foreground/30 hover:text-primary-foreground/60 transition-all">
                  {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                <button type="button" onClick={() => { navigator.clipboard.writeText(createPassword); toast.success("Password copied to clipboard"); }} className="p-1.5 rounded-lg hover:bg-primary-foreground/10 text-primary-foreground/30 hover:text-primary-foreground/60 transition-all">
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => setCreatePassword(generateRandomPassword())} className="p-1.5 rounded-lg hover:bg-primary-foreground/10 text-primary-foreground/30 hover:text-primary-foreground/60 transition-all">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <select value={createCompany} onChange={(e) => setCreateCompany(e.target.value)} required
              className="w-full px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm focus:outline-none focus:border-teal/40 transition-all">
              <option value="">Assign to company...</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="sm:col-span-2">
              <button type="submit" disabled={addAccount.isPending}
                className="px-6 py-3 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body disabled:opacity-50">
                {addAccount.isPending ? "Creating..." : `Create ${createRole === "employee" ? "Employee" : "Client"} Account`}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Assign Existing Client Section */}
      {showAssignSection && (
        <div className="glass-card rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-1">
            <UserCheck className="w-5 h-5 text-teal" />
            <h2 className="font-display text-lg text-primary-foreground">Assign Registered Clients</h2>
          </div>
          <p className="text-primary-foreground/30 text-sm font-body mb-5">
            Users who signed up themselves but haven't been linked to a company yet.
          </p>

          {unassignedLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
            </div>
          ) : unassigned.length === 0 ? (
            <div className="flex items-center gap-3 py-8 justify-center">
              <Users className="w-8 h-8 text-primary-foreground/15" />
              <p className="text-primary-foreground/30 text-sm font-body">All registered users are already assigned to a company.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {unassigned.map((u) => (
                <div key={u.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-primary-foreground/3 rounded-xl border border-primary-foreground/5">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-teal/60" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-primary-foreground/80 font-body truncate">{u.full_name || u.email}</p>
                      <p className="text-xs text-primary-foreground/30 font-body truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:flex-shrink-0">
                    <div className="relative flex-1 sm:w-44">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-primary-foreground/25 pointer-events-none" />
                      <select
                        value={assignCompanies[u.id] ?? ""}
                        onChange={(e) => setAssignCompanies((prev) => ({ ...prev, [u.id]: e.target.value }))}
                        className="w-full pl-9 pr-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-xs focus:outline-none focus:border-teal/40 transition-all appearance-none"
                      >
                        <option value="">Select company…</option>
                        {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <button
                      disabled={!assignCompanies[u.id] || doAssign.isPending}
                      onClick={() => {
                        const cid = assignCompanies[u.id];
                        if (cid) doAssign.mutate({ userId: u.id, companyId: cid });
                      }}
                      className="px-4 py-2 bg-teal/15 text-teal text-[11px] font-bold tracking-[1px] uppercase rounded-xl hover:bg-teal/25 transition-all font-body disabled:opacity-30 whitespace-nowrap"
                    >
                      Assign
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Users Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      ) : profiles.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center">
          <Users className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
          <p className="text-primary-foreground/40 font-body">No users yet.</p>
        </div>
      ) : (
        <div className="glass-card rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-primary-foreground/5">
                <th className="text-left px-6 py-4 text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/30 font-body">Name</th>
                <th className="text-left px-6 py-4 text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/30 font-body hidden md:table-cell">Company</th>
                <th className="text-left px-6 py-4 text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/30 font-body hidden sm:table-cell">Role</th>
                <th className="text-left px-6 py-4 text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/30 font-body hidden lg:table-cell">Last Login</th>
                <th className="px-6 py-4 text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/30 font-body text-right">Manage</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p, i) => {
                const role = getRoleLabel(p.user_id);
                const currentCompany = (p.companies as { name: string } | null)?.name ?? "—";
                const isEditing = editingCompany[p.user_id] !== undefined;
                const isExpanded = expandedUser === p.user_id;
                const perms = getPerms(p.user_id);
                return (
                  <>
                    <tr
                      key={p.id}
                      className={`border-b border-primary-foreground/5 transition-colors ${i === profiles.length - 1 && !isExpanded ? "border-0" : ""} ${isEditing ? "bg-teal/3" : "hover:bg-primary-foreground/3"}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-teal/15 flex items-center justify-center flex-shrink-0">
                            <span className="text-teal text-xs font-bold font-body">
                              {(p.full_name || "?")[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm text-primary-foreground/80 font-body">{p.full_name || "—"}</p>
                            <p className="text-xs text-primary-foreground/25 font-body font-mono truncate max-w-[180px]">{p.user_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={editingCompany[p.user_id]}
                              onChange={(e) => setEditingCompany((prev) => ({ ...prev, [p.user_id]: e.target.value }))}
                              className="px-3 py-1.5 bg-primary-foreground/5 border border-teal/30 rounded-lg text-primary-foreground font-body text-xs focus:outline-none transition-all"
                            >
                              <option value="">Select company…</option>
                              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                            <button
                              disabled={!editingCompany[p.user_id] || doReassign.isPending}
                              onClick={() => {
                                const cid = editingCompany[p.user_id];
                                if (cid) doReassign.mutate({ userId: p.user_id, companyId: cid });
                              }}
                              className="px-3 py-1.5 bg-teal/15 text-teal text-[10px] font-bold tracking-[1px] uppercase rounded-lg hover:bg-teal/25 transition-all font-body disabled:opacity-30"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingCompany((prev) => { const n = { ...prev }; delete n[p.user_id]; return n; })}
                              className="p-1.5 text-primary-foreground/20 hover:text-primary-foreground/60 transition-colors"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm text-primary-foreground/50 font-body">{currentCompany}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 hidden sm:table-cell">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-[1px] uppercase font-body ${roleBadge[role] ?? roleBadge.client}`}>
                          {role}
                        </span>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        {(() => {
                          const lastLogin = getLastLogin(p.user_id);
                          return lastLogin ? (
                            <div className="flex items-center gap-1.5">
                              <LogIn className="w-3 h-3 text-teal/50" />
                              <span className="text-sm text-primary-foreground/50 font-body">
                                {new Date(lastLogin).toLocaleDateString()} {new Date(lastLogin).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-primary-foreground/20 font-body italic">Never</span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          {role === "client" && !isEditing && (
                            <button
                              onClick={() => setEditingCompany((prev) => ({ ...prev, [p.user_id]: "" }))}
                              className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold tracking-[1px] uppercase text-primary-foreground/20 hover:text-teal hover:bg-teal/5 rounded-lg transition-all font-body"
                            >
                              <Building2 className="w-3 h-3" />
                              Move
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (resetPwUser === p.user_id) {
                                setResetPwUser(null);
                                setResetPwValue("");
                              } else {
                                setResetPwUser(p.user_id);
                                setResetPwValue(generateRandomPassword());
                                setShowResetPw(true);
                              }
                            }}
                            title="Reset password"
                            className="p-1.5 rounded-lg text-primary-foreground/20 hover:text-amber-400 hover:bg-amber-400/10 transition-all"
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setConfirmDelete(confirmDelete === p.user_id ? null : p.user_id)}
                            title="Delete account"
                            className="p-1.5 rounded-lg text-primary-foreground/20 hover:text-destructive hover:bg-destructive/10 transition-all"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setExpandedUser(isExpanded ? null : p.user_id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold tracking-[1px] uppercase rounded-lg transition-all font-body ${
                              isExpanded ? "bg-teal/15 text-teal" : "text-primary-foreground/30 hover:text-teal hover:bg-teal/5"
                            }`}
                          >
                            <Shield className="w-3 h-3" />
                            Permissions
                            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Delete confirmation */}
                    {confirmDelete === p.user_id && (
                      <tr key={`${p.id}-delete`} className="border-b border-primary-foreground/5">
                        <td colSpan={5} className="px-6 py-3">
                          <div className="flex items-center justify-between gap-4 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-xl">
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />
                              <p className="text-xs text-destructive font-body">
                                Permanently delete <strong>{p.full_name || p.user_id}</strong>? This cannot be undone.
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                onClick={() => setConfirmDelete(null)}
                                className="px-3 py-1.5 text-xs text-primary-foreground/40 hover:text-primary-foreground/70 font-body transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => doDelete.mutate(p.user_id)}
                                disabled={doDelete.isPending}
                                className="px-4 py-1.5 bg-destructive text-white text-xs font-bold font-body rounded-lg hover:bg-destructive/80 transition-all disabled:opacity-50"
                              >
                                {doDelete.isPending ? "Deleting…" : "Delete"}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Reset password */}
                    {resetPwUser === p.user_id && (
                      <tr key={`${p.id}-reset`} className="border-b border-primary-foreground/5">
                        <td colSpan={5} className="px-6 py-3">
                          <div className="flex items-center justify-between gap-4 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <KeyRound className="w-4 h-4 text-amber-400 flex-shrink-0" />
                              <p className="text-xs text-amber-300 font-body flex-shrink-0">New password for <strong>{p.full_name || p.user_id}</strong>:</p>
                              <div className="relative flex-1 max-w-xs">
                                <input
                                  type={showResetPw ? "text" : "password"}
                                  value={resetPwValue}
                                  onChange={(e) => setResetPwValue(e.target.value)}
                                  className="w-full px-3 py-1.5 bg-primary-foreground/5 border border-primary-foreground/10 rounded-lg text-primary-foreground font-body text-xs font-mono focus:outline-none focus:border-amber-400/40 transition-all pr-20"
                                />
                                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                                  <button type="button" onClick={() => setShowResetPw(!showResetPw)} className="p-1 rounded text-primary-foreground/30 hover:text-primary-foreground/60 transition-all">
                                    {showResetPw ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                  </button>
                                  <button type="button" onClick={() => { navigator.clipboard.writeText(resetPwValue); toast.success("Password copied"); }} className="p-1 rounded text-primary-foreground/30 hover:text-primary-foreground/60 transition-all">
                                    <Copy className="w-3 h-3" />
                                  </button>
                                  <button type="button" onClick={() => setResetPwValue(generateRandomPassword())} className="p-1 rounded text-primary-foreground/30 hover:text-primary-foreground/60 transition-all">
                                    <RefreshCw className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button onClick={() => { setResetPwUser(null); setResetPwValue(""); }} className="px-3 py-1.5 text-xs text-primary-foreground/40 hover:text-primary-foreground/70 font-body transition-colors">Cancel</button>
                              <button
                                onClick={() => doResetPassword.mutate({ userId: p.user_id, newPassword: resetPwValue })}
                                disabled={doResetPassword.isPending || resetPwValue.length < 6}
                                className="px-4 py-1.5 bg-amber-500 text-navy-deep text-xs font-bold font-body rounded-lg hover:bg-amber-400 transition-all disabled:opacity-50"
                              >
                                {doResetPassword.isPending ? "Resetting…" : "Reset Password"}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    {isExpanded && (
                      <tr key={`${p.id}-perms`} className="border-b border-primary-foreground/5 bg-primary-foreground/[0.02]">
                        <td colSpan={5} className="px-6 py-5">
                          <div className="flex flex-col lg:flex-row gap-6">
                            {/* Role section */}
                            <div className="lg:w-56">
                              <p className="text-[10px] font-bold tracking-[2px] uppercase text-primary-foreground/30 font-body mb-3">Change Role</p>
                              <select
                                value={role}
                                onChange={(e) => doUpdateRole.mutate({ userId: p.user_id, role: e.target.value })}
                                disabled={doUpdateRole.isPending}
                                className="w-full px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm focus:outline-none focus:border-teal/40 transition-all disabled:opacity-50"
                              >
                                <option value="client">Client</option>
                                <option value="employee">Employee</option>
                                <option value="admin">Admin</option>
                                <option value="auditor">Auditor (Flag State)</option>
                              </select>
                              <p className="text-[10px] text-primary-foreground/20 font-body mt-1.5">
                                Admins have full access. Auditors are read-only and restricted to the Auditor Portal (email must end with @register-iri.com).
                              </p>
                            </div>

                            {/* Page permissions section */}
                            <div className="flex-1">
                              <p className="text-[10px] font-bold tracking-[2px] uppercase text-primary-foreground/30 font-body mb-3">Page Visibility</p>
                              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                                {pageLabels.map(({ key, label }) => (
                                  <div key={key} className="flex items-center justify-between p-2.5 bg-primary-foreground/3 rounded-xl border border-primary-foreground/5">
                                    <span className="text-[11px] text-primary-foreground/60 font-body">{label}</span>
                                    <Switch
                                      checked={perms[key as keyof UserPerms] as boolean}
                                      onCheckedChange={(checked) => {
                                        const updated = { ...perms, [key]: checked };
                                        const permPayload: Record<string, boolean> = {};
                                        pageLabels.forEach(({ key: k }) => {
                                          permPayload[k] = updated[k as keyof UserPerms] as boolean;
                                        });
                                        doUpdatePerms.mutate({
                                          userId: p.user_id,
                                          permissions: permPayload,
                                        });
                                      }}
                                      disabled={doUpdatePerms.isPending}
                                    />
                                  </div>
                                ))}
                              </div>
                              <p className="text-[10px] text-primary-foreground/20 font-body mt-1.5">
                                Toggle which pages this user can see in their portal. Admins always see everything.
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
